import React, { useState, useEffect, useMemo } from "react";

// ─── CONFIG ───────────────────────────────────────────────────────────────────

const CONFIG = {
  SHEET_ID: "1oaqQsVh03s9Oybk-qia5AjkGXp4CmMVOcAtnXGJHX-4",
  API_KEY:  process.env.REACT_APP_SHEETS_API_KEY,
  TABS: {
    scorecard:    "Dashboard Score Card",
    costs:        "Dashboard Clean Project Costs",
    nonProject:   "Dashboard Clean Non-Project Expenses",
    revenue:      "Clean Total Revenue",
    coreInvoices: "Clean Total Revenue",
  },
  CLIENT:        { name: "Apex Creative", costTypes: ["billable", "internal"] },
  SPONSOR_MODEL: "S+C",
};

const YEAR = new Date().getFullYear().toString();

// ─── THEME ────────────────────────────────────────────────────────────────────

const LIGHT = {
  bg: "#EFEFEF", bgCard: "#E4E0D6", black: "#0A0B0D",
  orange: "#E67E50", green: "#5F7F74",
  muted: "#4A4A46", faint: "#D4D0C6", text: "#0A0B0D",
};
const DARK = {
  bg: "#0A0B0D", bgCard: "#1E1E1B", black: "#F5F1E8",
  orange: "#E67E50", green: "#5F7F74",
  muted: "#7A7A75", faint: "#232320", text: "#F5F1E8",
};

const mono = "'IBM Plex Mono', monospace";
const sans = "'DM Sans', sans-serif";

const FONTS = `
  @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500&family=DM+Sans:wght@400;500;600;700&display=swap');
  @keyframes slideIn { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
`;

// ─── TABLE COLUMNS ────────────────────────────────────────────────────────────

const SCORECARD_COLS = [
  { label: "Project",     w: "24%", key: "Project_Key" },
  { label: "Model",       w: "7%",  key: "Model" },
  { label: "Invoiced",    w: "10%", key: "Total_Revenue" },
  { label: "Funds In",    w: "10%", key: "Funds_In" },
  { label: "Total Costs", w: "11%", key: "Total_Costs_YTD" },
  { label: "True Margin", w: "11%", key: "True_Margin" },
  { label: "Margin",      w: "10%", key: "Margin_Pct" },
  { label: "Exposure",    w: "17%", key: "Exposure" },
];

const COST_COLS = [
  { label: "Project",  w: "14%", key: "Project_Key" },
  { label: "Vendor",   w: "16%", key: "Entity" },
  { label: "Source",   w: "6%",  key: "Source" },
  { label: "Date",     w: "9%",  key: "TxnDate" },
  { label: "Billable", w: "9%",  key: "Billable_Status" },
  { label: "Account",  w: "20%", key: "Account_Name" },
  { label: "Class",    w: "18%", key: "Class_Name" },
  { label: "Amount",   w: "8%",  key: "Amount" },
];

// ─── UTILITIES ────────────────────────────────────────────────────────────────

async function fetchSheet(tab, retries = 3) {
  const url = `https://sheets.googleapis.com/v4/spreadsheets/${CONFIG.SHEET_ID}/values/${encodeURIComponent(tab)}?key=${CONFIG.API_KEY}`;
  for (let i = 0; i < retries; i++) {
    const res = await fetch(url);
    if (res.ok) {
      const { values = [] } = await res.json();
      const [headers, ...rows] = values;
      return rows
        .filter(r => r.some(c => c !== ""))
        .map(r => headers.reduce((o, h, idx) => { o[h] = r[idx] ?? ""; return o; }, {}));
    }
    if (i < retries - 1) await new Promise(r => setTimeout(r, 1000));
  }
  throw new Error(`Failed to fetch ${tab}`);
}

const n       = v => parseFloat(v) || 0;
const fmtFull = v => { const num = n(v), abs = Math.abs(num); return (num < 0 ? "−" : "") + "$" + abs.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); };
const fmtK    = v => { const num = n(v); if (!num) return "—"; const abs = Math.abs(num), fmt = abs >= 1_000_000 ? `$${(abs/1_000_000).toFixed(1)}M` : abs >= 1000 ? `$${(abs/1000).toFixed(1)}K` : `$${abs.toFixed(0)}`; return num < 0 ? `−${fmt}` : fmt; };
const fmtPct  = v => { const num = n(v); return num ? `${(num * 100).toFixed(1)}%` : "—"; };

const costSubLabel = types =>
  types.includes("billable") && types.includes("internal") ? "billable + internal" :
  types.includes("billable") ? "billable costs" :
  types.includes("internal") ? "internal costs" : "total project costs";

function sortData(data, col, dir) {
  if (!col) return data;
  return [...data].sort((a, b) => {
    if (col === "Exposure") {
      const av = n(a[col]) < 0 ? Math.abs(n(a[col])) : -1;
      const bv = n(b[col]) < 0 ? Math.abs(n(b[col])) : -1;
      return dir === "asc" ? bv - av : av - bv;
    }
    if (col === "TxnDate" || col === "Date") {
      return dir === "asc"
        ? new Date(a[col] || 0) - new Date(b[col] || 0)
        : new Date(b[col] || 0) - new Date(a[col] || 0);
    }
    const av = parseFloat(a[col]), bv = parseFloat(b[col]);
    if (!isNaN(av) && !isNaN(bv)) return dir === "asc" ? av - bv : bv - av;
    if (!isNaN(av)) return -1;
    if (!isNaN(bv)) return 1;
    return dir === "asc"
      ? String(a[col] || "").localeCompare(String(b[col] || ""))
      : String(b[col] || "").localeCompare(String(a[col] || ""));
  });
}

// ─── SMALL COMPONENTS ─────────────────────────────────────────────────────────

function LoadingScreen() {
  return (
    <div style={{ background: "#0A0B0D", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <style>{FONTS}</style>
      <span style={{ fontFamily: mono, fontSize: "10px", color: "#7A7A75", letterSpacing: "0.15em" }}>
        Loading {CONFIG.CLIENT.name} data...
      </span>
    </div>
  );
}

function Logo({ t, size = 19 }) {
  return (
    <a href="https://ikifi.co" target="_blank" rel="noreferrer" style={{ display: "flex", alignItems: "baseline", textDecoration: "none" }}>
      <span style={{ fontFamily: sans, fontWeight: 700, fontSize: size, color: t.black, letterSpacing: "-0.02em" }}>ikifi</span>
      <span style={{ fontFamily: sans, fontWeight: 700, fontSize: size * 1.15, color: t.orange }}>.</span>
    </a>
  );
}

function SLabel({ children, t, noMargin }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: noMargin ? 0 : 14 }}>
      <span style={{ fontFamily: mono, fontSize: 9, letterSpacing: "0.14em", color: t.muted, textTransform: "uppercase", whiteSpace: "nowrap" }}>{children}</span>
      <div style={{ flex: 1, height: 1, background: t.faint }} />
    </div>
  );
}

function ResetSort({ show, onClick, t }) {
  if (!show) return null;
  return (
    <button onClick={onClick} style={{ fontFamily: mono, fontSize: 9, color: t.muted, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", letterSpacing: "0.08em", textTransform: "uppercase", whiteSpace: "nowrap" }}>
      Reset Sort
    </button>
  );
}

function DarkToggle({ dark, setDark, t }) {
  return (
    <button onClick={() => setDark(d => !d)} style={{ width: 38, height: 20, background: dark ? t.orange : t.faint, border: "none", cursor: "pointer", position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
      <div style={{ position: "absolute", top: 3, width: 14, height: 14, left: dark ? 21 : 3, transition: "left 0.2s", background: dark ? "#fff" : t.black }} />
    </button>
  );
}

function KPICard({ label, value, sub, alert, positive, t, mobile }) {
  const bg       = alert ? t.orange : t.bgCard;
  const col      = alert ? "#fff" : t.black;
  const scol     = alert ? "rgba(255,255,255,0.75)" : t.muted;
  const barColor = alert ? t.orange : positive === true ? t.green : positive === false ? t.orange : t.bgCard;
  return (
    <div style={{ flex: 1, minWidth: 0, background: bg, display: "flex", flexDirection: "column", transition: "transform 0.15s, box-shadow 0.15s" }}
      onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)"; }}
      onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; }}>
      <div style={{ height: 3, background: barColor }} />
      <div style={{ padding: mobile ? "20px 24px" : "18px 22px", display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ fontFamily: mono, fontSize: 9, letterSpacing: "0.13em", textTransform: "uppercase", color: scol }}>{label}</div>
        <div style={{ fontFamily: mono, fontSize: mobile ? 28 : 22, fontWeight: 500, color: col, letterSpacing: "-0.03em", lineHeight: 1 }}>{value}</div>
        <div style={{ fontFamily: mono, fontSize: 9, color: scol, minHeight: 12 }}>{sub || ""}</div>
      </div>
    </div>
  );
}

// ─── SELECT ───────────────────────────────────────────────────────────────────

function Sel({ value, onChange, options, placeholder, optionLabels = {}, t }) {
  const [open, setOpen]   = useState(false);
  const [query, setQuery] = useState("");
  const ref               = React.useRef(null);

  React.useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setQuery(""); } };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const label    = value ? (optionLabels[value] || value) : placeholder;
  const filtered = options.filter(o => (optionLabels[o] || o).toLowerCase().includes(query.toLowerCase()));

  return (
    <div ref={ref} style={{ position: "relative", minWidth: 190, userSelect: "none" }}>
      <div tabIndex={0}
        onClick={() => { setOpen(o => !o); setQuery(""); }}
        onKeyDown={e => {
          if (e.key === "Escape") { setOpen(false); setQuery(""); }
          else if (e.key === "Backspace") { setQuery(q => q.slice(0, -1)); setOpen(true); }
          else if (e.key.length === 1) { setQuery(q => q + e.key); setOpen(true); }
        }}
        style={{ fontFamily: mono, fontSize: 10, letterSpacing: "0.06em", color: value ? t.text : t.muted, background: t.bgCard, border: `1px solid ${t.faint}`, padding: "8px 14px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, outline: "none" }}>
        <span>{query || label}</span>
        <span style={{ fontSize: 8, color: t.muted, transform: open ? "rotate(180deg)" : "", transition: "transform 0.15s" }}>▼</span>
      </div>
      {open && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100, background: t.bgCard, border: `1px solid ${t.faint}`, borderTop: "none", maxHeight: 260, overflowY: "auto" }}>
          <div onClick={() => { onChange(""); setQuery(""); setOpen(false); }}
            style={{ fontFamily: mono, fontSize: 10, padding: "8px 14px", cursor: "pointer", color: !value ? t.orange : t.muted, background: !value ? `${t.orange}10` : "transparent", borderBottom: `1px solid ${t.faint}` }}>
            {placeholder}
          </div>
          {filtered.map(o => (
            <div key={o} onClick={() => { onChange(o); setQuery(""); setOpen(false); }}
              style={{ fontFamily: mono, fontSize: 10, padding: "8px 14px", cursor: "pointer", color: value === o ? t.orange : t.text, background: value === o ? `${t.orange}10` : "transparent", borderBottom: `1px solid ${t.faint}` }}
              onMouseEnter={e => { if (value !== o) e.currentTarget.style.background = t.faint; }}
              onMouseLeave={e => { if (value !== o) e.currentTarget.style.background = "transparent"; }}>
              {optionLabels[o] || o}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── CASH BAR ─────────────────────────────────────────────────────────────────

function CashBar({ d, t, maxAmt }) {
  const [tip, setTip] = useState(null);
  const col = n(d.c), out = n(d.o), exp = Math.abs(n(d.u)), inv = n(d.invoiced);
  const hasExp = n(d.u) < 0;
  const scale = 90 / Math.max(col + out + exp, inv, maxAmt, 1);
  const cW = col * scale, oW = out * scale, tW = inv * scale, eW = hasExp ? exp * scale : 0;

  const seg = (style, label, amt) => ({
    style,
    onMouseEnter: () => setTip({ label, amt }),
    onMouseLeave: () => setTip(null),
  });

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 10 }}>
      <div style={{ width: 200, textAlign: "right", fontFamily: mono, fontSize: 10, color: t.muted, flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.name}</div>
      <div style={{ flex: 1, position: "relative", height: 16 }}>
        {tW > 0  && <div style={{ position: "absolute", inset: 0, width: `${tW}%`, background: t.faint }} />}
        {col > 0 && <div {...seg({ position: "absolute", inset: 0, width: `${cW}%`, background: t.black, transition: "transform 0.15s", transform: tip?.label === "Collected" ? "scaleY(1.5)" : "", transformOrigin: "center" }, "Collected", col)} />}
        {out > 0 && <div {...seg({ position: "absolute", top: 0, bottom: 0, left: `${cW}%`, width: `${oW}%`, backgroundImage: `radial-gradient(circle, ${t.muted} 1.5px, transparent 1.5px)`, backgroundSize: "6px 6px", transition: "transform 0.15s", transform: tip?.label === "Outstanding" ? "translateY(-3px)" : "" }, "Outstanding", out)} />}
        {hasExp  && <div {...seg({ position: "absolute", top: 0, bottom: 0, left: `${cW + oW}%`, width: `${eW}%`, background: t.orange, transition: "transform 0.15s", transform: tip?.label === "Underfunded" ? "scaleY(1.5)" : "", transformOrigin: "center" }, "Underfunded", exp)} />}
        {tip && (
          <div style={{ position: "absolute", bottom: 22, left: "35%", transform: "translateX(-50%)", background: t.black, color: t.bg, fontFamily: mono, fontSize: 10, padding: "4px 10px", whiteSpace: "nowrap", pointerEvents: "none", zIndex: 10 }}>
            {tip.label} · {fmtFull(tip.amt)}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── SCROLL TABLE ─────────────────────────────────────────────────────────────

function ScrollTable({ headers, rows, footer, t, maxHeight = "55vh", sortCol, sortDir, onSort, dark }) {
  return (
    <div style={{ border: `1px solid ${t.faint}`, overflow: "hidden" }}>
      <div style={{ display: "flex", padding: "10px 16px", background: dark ? "#1E1E1C" : t.black, borderBottom: `1px solid ${t.faint}`, borderLeft: "4px solid transparent" }}>
        {headers.map((h, i) => (
          <div key={i} onClick={() => h.key && onSort(h.key)}
            style={{ width: h.w, flexShrink: 0, fontFamily: mono, fontSize: 9, letterSpacing: "0.1em", textTransform: "uppercase", color: sortCol === h.key ? t.orange : "#9B9891", cursor: h.key ? "pointer" : "default", display: "flex", alignItems: "center", gap: 4, userSelect: "none" }}>
            {h.label}
            {h.info && (
              <span style={{ position: "relative", display: "inline-flex", alignItems: "center" }}
                onMouseEnter={e => e.currentTarget.querySelector(".tip").style.display = "block"}
                onMouseLeave={e => e.currentTarget.querySelector(".tip").style.display = "none"}>
                <span style={{ marginLeft: 4, fontSize: 11, color: t.orange, cursor: "help", fontWeight: 600 }}>ⓘ</span>
                <span className="tip" style={{ display: "none", position: "absolute", top: 18, left: 0, background: t.orange, color: "#fff", fontFamily: mono, fontSize: 9, padding: "6px 10px", whiteSpace: "nowrap", zIndex: 50, pointerEvents: "none" }}>{h.info}</span>
              </span>
            )}
            {h.key && <span style={{ opacity: sortCol === h.key ? 1 : 0.3, fontSize: 8 }}>{sortCol === h.key ? (sortDir === "asc" ? "↑" : "↓") : "↕"}</span>}
          </div>
        ))}
      </div>
      <div style={{ maxHeight, overflowY: "auto" }}>{rows}</div>
      {footer && <div style={{ borderTop: `1px solid ${t.faint}`, background: t.bgCard }}>{footer}</div>}
    </div>
  );
}

// ─── DROPDOWN CELL ────────────────────────────────────────────────────────────

function DropCell({ w, trigger, children }) {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div ref={ref} style={{ width: w, flexShrink: 0, position: "relative" }}>
      <div onClick={e => { e.stopPropagation(); setOpen(o => !o); }} style={{ cursor: "pointer" }}>
        {trigger(open)}
      </div>
      {open && <div onClick={e => e.stopPropagation()} style={{ position: "absolute", top: "100%", left: 0, zIndex: 200, marginTop: 4 }}>{children}</div>}
    </div>
  );
}

// ─── INVOICED CELL ────────────────────────────────────────────────────────────

function InvoicedCell({ project, total, lines, t, w }) {
  const rows = lines.filter(l => l.Project_Key === project);
  if (!n(total)) return <div style={{ width: w, flexShrink: 0, fontFamily: mono, fontSize: 11, color: t.muted }}>—</div>;
  return (
    <DropCell w={w}
      trigger={open => (
        <div style={{ fontFamily: mono, fontSize: 11, color: t.black, display: "flex", alignItems: "center", gap: 5, userSelect: "none" }}>
          {fmtK(total)}
          {rows.length > 0 && <span style={{ fontSize: 8, color: open ? "#fff" : t.muted, background: open ? t.orange : t.faint, padding: "1px 5px", transition: "background 0.15s, color 0.15s" }}>{rows.length}</span>}
        </div>
      )}>
      {rows.length > 0 && (
        <div style={{ background: t.bgCard, border: `1px solid ${t.faint}`, minWidth: 380, boxShadow: "0 4px 16px rgba(0,0,0,0.12)" }}>
          <div style={{ padding: "8px 12px", borderBottom: `1px solid ${t.faint}`, fontFamily: mono, fontSize: 8, letterSpacing: "0.12em", textTransform: "uppercase", color: t.muted, display: "flex", justifyContent: "space-between" }}>
            <span>Invoice Breakdown · {project}</span><span>{rows.length} line{rows.length !== 1 ? "s" : ""}</span>
          </div>
          <div style={{ maxHeight: 240, overflowY: "auto" }}>
            {rows.map((l, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderBottom: i < rows.length - 1 ? `1px solid ${t.faint}` : "none", gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: mono, fontSize: 10, color: t.black, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.Description || "—"}</div>
                  <div style={{ fontFamily: mono, fontSize: 8, color: t.muted, marginTop: 2 }}>{l.DocNumber ? `#${l.DocNumber} · ` : ""}{l.TxnDate || ""}{l.Account_Name ? ` · ${l.Account_Name.split(":").pop().trim()}` : ""}</div>
                </div>
                <div style={{ fontFamily: mono, fontSize: 11, color: t.black, fontWeight: 500, flexShrink: 0 }}>{fmtFull(l.Amount)}</div>
              </div>
            ))}
          </div>
          <div style={{ padding: "8px 12px", borderTop: `1px solid ${t.faint}`, display: "flex", justifyContent: "space-between", background: t.bg }}>
            <span style={{ fontFamily: mono, fontSize: 8, color: t.muted, letterSpacing: "0.1em", textTransform: "uppercase" }}>Total</span>
            <span style={{ fontFamily: mono, fontSize: 12, color: t.black, fontWeight: 700 }}>{fmtFull(rows.reduce((s, l) => s + n(l.Amount), 0))}</span>
          </div>
        </div>
      )}
    </DropCell>
  );
}

// ─── FUNDS IN CELL ────────────────────────────────────────────────────────────

function FundsInCell({ project, total, lines, t, w }) {
  const rows = lines.filter(l => l.Project_Key === project);
  const invMap = {};
  rows.forEach(l => {
    const d = l.DocNumber || "no-doc";
    if (!invMap[d]) invMap[d] = { DocNumber: l.DocNumber, TxnDate: l.TxnDate, DueDate: l.DueDate, Balance: n(l.Balance), Amount: 0, Description: l.Description };
    invMap[d].Amount += n(l.Amount);
  });
  const invoices  = Object.values(invMap);
  const paidCount = invoices.filter(i => i.Balance === 0).length;
  const openCount = invoices.filter(i => i.Balance > 0).length;

  if (!n(total)) return <div style={{ width: w, flexShrink: 0, fontFamily: mono, fontSize: 11, color: t.muted }}>—</div>;
  return (
    <DropCell w={w}
      trigger={open => (
        <div style={{ fontFamily: mono, fontSize: 11, color: t.black, display: "flex", alignItems: "center", gap: 5, userSelect: "none" }}>
          {fmtK(total)}
          {invoices.length > 0 && <span style={{ fontSize: 8, color: open ? "#fff" : t.muted, background: open ? t.green : t.faint, padding: "1px 5px", transition: "background 0.15s, color 0.15s" }}>{paidCount}</span>}
        </div>
      )}>
      {invoices.length > 0 && (
        <div style={{ background: t.bgCard, border: `1px solid ${t.faint}`, minWidth: 420, boxShadow: "0 4px 16px rgba(0,0,0,0.12)" }}>
          <div style={{ padding: "8px 12px", borderBottom: `1px solid ${t.faint}`, fontFamily: mono, fontSize: 8, letterSpacing: "0.12em", textTransform: "uppercase", color: t.muted, display: "flex", justifyContent: "space-between" }}>
            <span>Payment Status · {project}</span><span>{paidCount} paid · {openCount} outstanding</span>
          </div>
          <div style={{ maxHeight: 240, overflowY: "auto" }}>
            {invoices.map((inv, i) => {
              const paid = inv.Balance === 0;
              return (
                <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderBottom: i < invoices.length - 1 ? `1px solid ${t.faint}` : "none", borderLeft: `3px solid ${paid ? t.green : t.orange}`, gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: mono, fontSize: 10, color: t.black, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{inv.Description || "—"}</div>
                    <div style={{ fontFamily: mono, fontSize: 8, color: t.muted, marginTop: 2 }}>{inv.DocNumber ? `#${inv.DocNumber} · ` : ""}{inv.TxnDate || ""}{inv.DueDate ? ` · Due ${inv.DueDate}` : ""}</div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontFamily: mono, fontSize: 11, color: t.black, fontWeight: 500 }}>{fmtFull(inv.Amount)}</div>
                    <div style={{ fontFamily: mono, fontSize: 8, color: paid ? t.green : t.orange, marginTop: 2 }}>{paid ? "Paid" : `${fmtFull(inv.Balance)} outstanding`}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ padding: "8px 12px", borderTop: `1px solid ${t.faint}`, display: "flex", justifyContent: "space-between", background: t.bg }}>
            <span style={{ fontFamily: mono, fontSize: 8, color: t.muted, letterSpacing: "0.1em", textTransform: "uppercase" }}>Collected</span>
            <span style={{ fontFamily: mono, fontSize: 12, color: t.black, fontWeight: 700 }}>{fmtFull(n(total))}</span>
          </div>
        </div>
      )}
    </DropCell>
  );
}

// ─── APP ──────────────────────────────────────────────────────────────────────

export default function App() {

  // UI state
  const [dark, setDark]             = useState(() => window.matchMedia("(prefers-color-scheme: dark)").matches);
  const [tab, setTab]               = useState("overview");
  const [hoveredRow, setHoveredRow] = useState(null);
  const [isMobile, setIsMobile]     = useState(window.innerWidth <= 768);

  // Data
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [scorecard, setScorecard]   = useState([]);
  const [costs, setCosts]           = useState([]);
  const [nonProject, setNonProject] = useState([]);
  const [revenueLines, setRevLines] = useState([]);
  const [coreInvoices, setCoreInv]  = useState([]);

  // Filters
  const [projFilter, setProjFilter] = useState("");
  const [billFilter, setBillFilter] = useState("");
  const [search, setSearch]         = useState("");

  // Sort
  const [sSortCol, setSSortCol] = useState(() => { const s = localStorage.getItem("ikifi_scoreSort"); return s ? JSON.parse(s).col : "Total_Revenue"; });
  const [sSortDir, setSSortDir] = useState(() => { const s = localStorage.getItem("ikifi_scoreSort"); return s ? JSON.parse(s).dir : "desc"; });
  const [cSortCol, setCSortCol] = useState(() => { const s = localStorage.getItem("ikifi_costSort");  return s ? JSON.parse(s).col : "TxnDate"; });
  const [cSortDir, setCSortDir] = useState(() => { const s = localStorage.getItem("ikifi_costSort");  return s ? JSON.parse(s).dir : "desc"; });

  // ─── EFFECTS ────────────────────────────────────────────────────────────────

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  useEffect(() => { localStorage.setItem("ikifi_scoreSort", JSON.stringify({ col: sSortCol, dir: sSortDir })); }, [sSortCol, sSortDir]);
  useEffect(() => { localStorage.setItem("ikifi_costSort",  JSON.stringify({ col: cSortCol, dir: cSortDir })); }, [cSortCol, cSortDir]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [sc, co, np, rv, ci] = await Promise.all([
          fetchSheet(CONFIG.TABS.scorecard),
          fetchSheet(CONFIG.TABS.costs),
          fetchSheet(CONFIG.TABS.nonProject),
          fetchSheet(CONFIG.TABS.revenue),
          fetchSheet(CONFIG.TABS.coreInvoices),
        ]);
        setScorecard(sc); setCosts(co); setNonProject(np); setRevLines(rv); setCoreInv(ci);
      } catch (e) { setError(e.message); }
      finally { setLoading(false); }
    })();
  }, []);

  // ─── DERIVED DATA ───────────────────────────────────────────────────────────

  const t = dark ? DARK : LIGHT;

  const allScorecard = useMemo(() =>
    scorecard.filter(p => p.Project_Key && p.Year === YEAR), [scorecard]);

  const coreScorecard = useMemo(() =>
    allScorecard.filter(p => p.Model !== CONFIG.SPONSOR_MODEL), [allScorecard]);

  const sponsorRows = useMemo(() =>
    allScorecard.filter(p => p.Model === CONFIG.SPONSOR_MODEL), [allScorecard]);

  const sortedScorecard = useMemo(() =>
    [...sponsorRows, ...sortData(coreScorecard, sSortCol, sSortDir)],
    [sponsorRows, coreScorecard, sSortCol, sSortDir]);

  const filteredCosts      = useMemo(() => costs.filter(c => c.Year === YEAR), [costs]);
  const filteredNonProject = useMemo(() => nonProject.filter(c => c.Year === YEAR), [nonProject]);

  const kpis = useMemo(() => {
    const activeProjects = coreScorecard.length;
    const totalInvoiced  = allScorecard.reduce((s, p) => s + n(p.Total_Revenue), 0);
    const totalFundsIn   = allScorecard.reduce((s, p) => s + n(p.Funds_In), 0);
    const totalCosts     = coreScorecard.reduce((s, p) => s + n(p.Total_Costs_YTD), 0);
    const netFundsHeld   = totalFundsIn - totalCosts;
    const totalExposure  = coreScorecard.reduce((s, p) => s + (n(p.Exposure) < 0 ? n(p.Exposure) : 0), 0);
    const collectionRate = totalInvoiced > 0 ? ((totalFundsIn / totalInvoiced) * 100).toFixed(1) : "0";
    return { activeProjects, totalInvoiced, totalFundsIn, totalCosts, netFundsHeld, totalExposure, collectionRate };
  }, [allScorecard, coreScorecard]);

  const cashGapData = useMemo(() =>
    coreScorecard
      .filter(p => n(p.Funds_In) > 0 || n(p.Core_Outstanding) > 0 || n(p.Sponsorship_Outstanding) > 0 || n(p.Exposure) < 0)
      .map(p => ({ name: p.Project_Key, c: n(p.Funds_In), o: n(p.Core_Outstanding) + n(p.Sponsorship_Outstanding), u: n(p.Exposure) < 0 ? n(p.Exposure) : 0, invoiced: n(p.Total_Revenue) }))
      .sort((a, b) => n(b.o) - n(a.o) || n(b.c) - n(a.c) || Math.abs(n(b.u)) - Math.abs(n(a.u))),
    [coreScorecard]);

  const cashGapMax = useMemo(() => Math.max(...cashGapData.map(d => n(d.invoiced)), 1), [cashGapData]);

  const isNoneFilter = projFilter === "__none__";
  const projOptions  = [...new Set(filteredCosts.map(c => c.Project_Key))].filter(Boolean).sort();

  const filteredCostRows = useMemo(() => {
    const src = isNoneFilter ? filteredNonProject : filteredCosts;
    return src.filter(c =>
      (isNoneFilter || !projFilter || c.Project_Key === projFilter) &&
      (!billFilter || c.Billable_Status === billFilter) &&
      (!search || [c.Entity, c["VendorRef.name"], c.Account_Name, c.Class_Name].some(v => v?.toLowerCase().includes(search.toLowerCase())))
    );
  }, [projFilter, billFilter, search, isNoneFilter, filteredCosts, filteredNonProject]);

  const sortedCosts  = useMemo(() => sortData(filteredCostRows, cSortCol, cSortDir), [filteredCostRows, cSortCol, cSortDir]);
  const totalCostAmt = useMemo(() => sortedCosts.reduce((s, c) => s + n(c.Amount || c["Line.Amount"] || 0), 0), [sortedCosts]);

  const exportCSV = () => {
    const hdrs = ["Project","Vendor","Source","Date","Billable Status","Account","Class","Amount"];
    const rows = sortedCosts.map(c => [
      c.Project_Key||"—", c.Entity||c["VendorRef.name"]||"",
      c.Source||"", c.TxnDate||c.Date||"",
      c.Billable_Status||"", c.Account_Name||"",
      c.Class_Name||"", c.Amount||"",
    ]);
    const csv = [hdrs, ...rows].map(r => r.map(v => `"${v}"`).join(",")).join("\n");
    Object.assign(document.createElement("a"), {
      href: URL.createObjectURL(new Blob([csv], { type: "text/csv" })),
      download: `${CONFIG.CLIENT.name.toLowerCase().replace(/\s+/g, "-")}-costs-${YEAR}.csv`,
    }).click();
  };

  const handleScoreSort = k => { if (sSortCol === k) setSSortDir(d => d === "asc" ? "desc" : "asc"); else { setSSortCol(k); setSSortDir("desc"); } };
  const handleCostSort  = k => { if (cSortCol === k) setCSortDir(d => d === "asc" ? "desc" : "asc"); else { setCSortCol(k); setCSortDir("asc"); } };
  const resetScoreSort  = () => { setSSortCol("Total_Revenue"); setSSortDir("desc"); };
  const resetCostSort   = () => { setCSortCol("TxnDate"); setCSortDir("desc"); };

  // ─── LOADING / ERROR ────────────────────────────────────────────────────────

  if (loading) return <LoadingScreen />;
  if (error) return (
    <div style={{ background: t.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 12 }}>
      <style>{FONTS}</style>
      <div style={{ fontFamily: mono, fontSize: 11, color: t.orange }}>Dashboard error.</div>
      <div style={{ fontFamily: mono, fontSize: 10, color: t.muted }}>Contact <a href="mailto:jordan@ikifi.co" style={{ color: t.orange, textDecoration: "none" }}>jordan@ikifi.co</a></div>
    </div>
  );

  // ─── SCORECARD ROWS ─────────────────────────────────────────────────────────

  const scorecardRows = sortedScorecard.map((p, i) => {
    const isSponsor   = p.Model === CONFIG.SPONSOR_MODEL;
    const marginPct   = n(p.Margin_Pct);
    const hasMargin   = p.Margin_Pct !== "" && p.Margin_Pct !== "0" && p.Margin_Pct !== undefined;
    const hasExposure = p.Exposure !== "" && n(p.Exposure) < 0;
    const isGreen     = hasMargin && marginPct >= 0.70;
    const mc = !hasMargin ? t.muted : isGreen ? t.green : t.orange;
    const lb = isSponsor ? t.orange : hasMargin ? (isGreen ? t.green : t.orange) : hasExposure ? t.orange : "transparent";
    const active = n(p.Total_Revenue) !== 0 || n(p.Total_Costs_YTD) !== 0 || hasExposure;

    return (
      <div key={`s${i}`}
        onMouseEnter={() => setHoveredRow(`s${i}`)} onMouseLeave={() => setHoveredRow(null)}
        style={{ display: "flex", alignItems: "center", padding: "11px 16px", borderBottom: `1px solid ${t.faint}`, borderLeft: `4px solid ${lb}`, background: isSponsor || hoveredRow === `s${i}` ? `${t.orange}08` : i % 2 === 0 ? t.bg : "transparent", transition: "background 0.15s", opacity: active ? 1 : 0.35 }}>
        <div style={{ width: SCORECARD_COLS[0].w, flexShrink: 0, fontFamily: sans, fontSize: 13, fontWeight: 600, color: isSponsor ? t.orange : t.black, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingRight: 10 }}>{p.Project_Key}</div>
        <div style={{ width: SCORECARD_COLS[1].w, flexShrink: 0 }}>
          {p.Model
            ? <span style={{ fontFamily: mono, fontSize: 8, letterSpacing: "0.08em", color: p.Model === "HF" ? t.bg : isSponsor ? "#fff" : t.black, background: p.Model === "HF" ? t.black : isSponsor ? t.orange : "transparent", border: `1px solid ${isSponsor ? t.orange : t.black}`, padding: "2px 5px" }}>{p.Model}</span>
            : <span style={{ color: t.muted }}>—</span>}
        </div>
        <InvoicedCell project={p.Project_Key} total={p.Total_Revenue} lines={revenueLines} t={t} w={SCORECARD_COLS[2].w} />
        <FundsInCell  project={p.Project_Key} total={p.Funds_In}      lines={coreInvoices} t={t} w={SCORECARD_COLS[3].w} />
        <div style={{ width: SCORECARD_COLS[4].w, flexShrink: 0, fontFamily: mono, fontSize: 11, color: t.black }}>{n(p.Total_Costs_YTD) ? fmtK(p.Total_Costs_YTD) : <span style={{ color: t.muted }}>—</span>}</div>
        <div style={{ width: SCORECARD_COLS[5].w, flexShrink: 0, fontFamily: mono, fontSize: 11, color: t.black }}>{n(p.True_Margin) ? fmtK(p.True_Margin) : <span style={{ color: t.muted }}>—</span>}</div>
        <div style={{ width: SCORECARD_COLS[6].w, flexShrink: 0, fontFamily: mono, fontSize: 13, fontWeight: 700, color: mc }}>{hasMargin ? fmtPct(p.Margin_Pct) : <span style={{ color: t.muted }}>—</span>}</div>
        <div style={{ width: SCORECARD_COLS[7].w, flexShrink: 0, fontFamily: mono, fontSize: 13, fontWeight: 600, color: hasExposure ? t.orange : t.muted }}>{hasExposure ? `−${fmtK(Math.abs(n(p.Exposure)))}` : "—"}</div>
      </div>
    );
  });

  const scorecardFooter = (
    <div style={{ display: "flex", padding: "13px 16px" }}>
      <div style={{ width: SCORECARD_COLS[0].w, flexShrink: 0, fontFamily: mono, fontSize: 10, color: t.black, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em" }}>Grand Total</div>
      <div style={{ width: SCORECARD_COLS[1].w, flexShrink: 0 }} />
      <div style={{ width: SCORECARD_COLS[2].w, flexShrink: 0, fontFamily: mono, fontSize: 12, fontWeight: 700, color: t.black }}>{fmtK(kpis.totalInvoiced)}</div>
      <div style={{ width: SCORECARD_COLS[3].w, flexShrink: 0, fontFamily: mono, fontSize: 12, fontWeight: 700, color: t.black }}>{fmtK(kpis.totalFundsIn)}</div>
      <div style={{ width: SCORECARD_COLS[4].w, flexShrink: 0, fontFamily: mono, fontSize: 12, fontWeight: 700, color: t.black }}>{fmtK(kpis.totalCosts)}</div>
      <div style={{ width: SCORECARD_COLS[5].w, flexShrink: 0, fontFamily: mono, fontSize: 12, fontWeight: 700, color: t.black }}>{fmtK(allScorecard.reduce((s, p) => s + n(p.True_Margin), 0))}</div>
      <div style={{ width: SCORECARD_COLS[6].w, flexShrink: 0, fontFamily: mono, fontSize: 12, fontWeight: 700, color: t.muted }}>—</div>
      <div style={{ width: SCORECARD_COLS[7].w, flexShrink: 0, fontFamily: mono, fontSize: 12, fontWeight: 700, color: t.orange }}>{kpis.totalExposure < 0 ? `−${fmtK(Math.abs(kpis.totalExposure))}` : "—"}</div>
    </div>
  );

  // ─── COST ROWS ──────────────────────────────────────────────────────────────

  const costRows = sortedCosts.map((c, i) => {
    const isNB   = c.Billable_Status === "NotBillable";
    const vendor = (c.Entity || c["VendorRef.name"] || c.Vendor || "").toLowerCase().replace(/\b\w/g, l => l.toUpperCase());

    return (
      <div key={`c${i}`}
        onMouseEnter={() => setHoveredRow(`c${i}`)} onMouseLeave={() => setHoveredRow(null)}
        style={{
          display: "flex", alignItems: "center", padding: "10px 16px",
          borderBottom: `1px solid ${t.faint}`,
          borderLeft: `4px solid ${isNB ? t.orange : t.green}`,
          background: hoveredRow === `c${i}` ? `${t.orange}08` : i % 2 === 0 ? t.bg : "transparent",
          transition: "background 0.15s",
        }}>
        <div style={{ width: COST_COLS[0].w, flexShrink: 0, fontFamily: sans, fontSize: 11, fontWeight: 600, color: c.Project_Key ? t.black : t.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingRight: 8 }}>{c.Project_Key || "—"}</div>
        <div style={{ width: COST_COLS[1].w, flexShrink: 0, fontFamily: mono, fontSize: 10, color: t.black, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingRight: 8 }}>{vendor}</div>
        <div style={{ width: COST_COLS[2].w, flexShrink: 0, fontFamily: mono, fontSize: 10, color: t.black }}>{c.Source || ""}</div>
        <div style={{ width: COST_COLS[3].w, flexShrink: 0, fontFamily: mono, fontSize: 10, color: t.black }}>{c.TxnDate || c.Date || ""}</div>
        <div style={{ width: COST_COLS[4].w, flexShrink: 0 }}>
          <span style={{ fontFamily: mono, fontSize: 8, letterSpacing: "0.08em", padding: "2px 6px", border: `1px solid ${isNB ? t.orange : t.green}`, color: isNB ? t.orange : t.green }}>{c.Billable_Status || "—"}</span>
        </div>
        <div style={{ width: COST_COLS[5].w, flexShrink: 0, fontFamily: mono, fontSize: 10, color: t.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingRight: 8 }}>{c.Account_Name || c.Account || ""}</div>
        <div style={{ width: COST_COLS[6].w, flexShrink: 0, fontFamily: mono, fontSize: 10, color: t.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingRight: 8 }}>{c.Class_Name || "—"}</div>
        <div style={{ width: COST_COLS[7].w, flexShrink: 0, fontFamily: mono, fontSize: 11, fontWeight: 500, color: t.black }}>{c.Amount || c["Line.Amount"] ? fmtFull(c.Amount || c["Line.Amount"]) : "—"}</div>
      </div>
    );
  });

  const costFooter = (
    <div style={{ display: "flex", justifyContent: "flex-end", padding: "14px 16px", gap: 20 }}>
      <span style={{ fontFamily: mono, fontSize: 9, color: t.muted, letterSpacing: "0.1em", textTransform: "uppercase", alignSelf: "center" }}>Total</span>
      <span style={{ fontFamily: mono, fontSize: 16, color: t.black, fontWeight: 500 }}>{fmtFull(totalCostAmt)}</span>
    </div>
  );

  // ─── SHARED ─────────────────────────────────────────────────────────────────

  const pageStyle = { background: t.bg, minHeight: "100vh", fontFamily: sans, transition: "background 0.2s, color 0.2s", animation: "fadeIn 0.5s ease-in", overflowX: "hidden" };
  const pageFooter = (
    <div style={{ marginTop: 60, paddingTop: 20, borderTop: `1px solid ${t.faint}`, display: "flex", justifyContent: "space-between" }}>
      <span style={{ fontFamily: mono, fontSize: 9, color: t.muted }}>© ikifi {YEAR}</span>
      <span style={{ fontFamily: mono, fontSize: 9, color: t.muted }}>Clarity, by design.</span>
    </div>
  );

  // ─── MOBILE ─────────────────────────────────────────────────────────────────

  if (isMobile) return (
    <div style={pageStyle}>
      <style>{FONTS}</style>
      <nav style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: `1px solid ${t.faint}` }}>
        <Logo t={t} size={22} />
        <DarkToggle dark={dark} setDark={setDark} t={t} />
      </nav>
      <div style={{ padding: "24px 20px 48px", display: "flex", flexDirection: "column", gap: 2 }}>
        <div style={{ fontFamily: mono, fontSize: 8, letterSpacing: "0.14em", color: t.muted, textTransform: "uppercase", marginBottom: 12 }}>{CONFIG.CLIENT.name} · FY{YEAR}</div>
        <KPICard t={t} mobile label="Invoiced Total"       value={fmtK(kpis.totalInvoiced)}  sub={`${kpis.activeProjects} active projects`} />
        <KPICard t={t} mobile label="Cash Collected"       value={fmtK(kpis.totalFundsIn)}   sub={`${kpis.collectionRate}% of invoiced`} positive={true} />
        <KPICard t={t} mobile label="Project Costs"        value={fmtK(kpis.totalCosts)}     sub={costSubLabel(CONFIG.CLIENT.costTypes)} positive={false} />
        <KPICard t={t} mobile label="Net Funds Held"       value={fmtK(kpis.netFundsHeld)}   sub="cash collected − allocated costs" positive={kpis.netFundsHeld > 0} />
        <KPICard t={t} mobile label="Underfunded Exposure" value={kpis.totalExposure < 0 ? `-${fmtK(Math.abs(kpis.totalExposure))}` : "$0"} sub={`${coreScorecard.filter(p => n(p.Exposure) < 0).length} projects underfunded`} alert />
      </div>
      <div style={{ padding: "0 20px 32px" }}>{pageFooter}</div>
    </div>
  );

  // ─── DESKTOP ────────────────────────────────────────────────────────────────

  return (
    <div style={pageStyle}>
      <style>{FONTS}</style>

      <nav style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", padding: "20px 48px", borderBottom: `1px solid ${t.faint}`, background: t.bg }}>
        <Logo t={t} size={29} />
        <div style={{ display: "flex", gap: 2, background: t.bgCard, padding: 3 }}>
          {[["overview", "Project Overview"], ["costs", "Project Costs"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} style={{ fontFamily: mono, fontSize: 9, letterSpacing: "0.1em", textTransform: "uppercase", padding: "7px 18px", border: "none", cursor: "pointer", transition: "all 0.15s", background: tab === k ? t.orange : "transparent", color: tab === k ? "#fff" : t.black }}>{l}</button>
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 20, justifyContent: "flex-end" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
              <span style={{ fontFamily: mono, fontSize: 11, color: t.black, fontWeight: 500 }}>{CONFIG.CLIENT.name}</span>
              <span style={{ fontFamily: mono, fontSize: 8, color: t.muted }}>FY{YEAR}</span>
            </div>
            <span style={{ fontFamily: mono, fontSize: 8, color: t.muted, opacity: 0.7 }}>
              Synced · {(() => { const d = new Date(); d.setMinutes(0,0,0); return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }); })()} · auto-syncs hourly
            </span>
          </div>
          <DarkToggle dark={dark} setDark={setDark} t={t} />
        </div>
      </nav>

      <div style={{ padding: "40px 48px 64px" }}>

        {/* ── OVERVIEW ── */}
        {tab === "overview" && <>
          <div style={{ display: "flex", gap: 2, marginBottom: 48 }}>
            <KPICard t={t} label="Invoiced Total"       value={fmtK(kpis.totalInvoiced)}  sub={`${kpis.activeProjects} active projects`} />
            <KPICard t={t} label="Cash Collected"       value={fmtK(kpis.totalFundsIn)}   sub={`${kpis.collectionRate}% of invoiced`} positive={true} />
            <KPICard t={t} label="Project Costs"        value={fmtK(kpis.totalCosts)}     sub={costSubLabel(CONFIG.CLIENT.costTypes)} positive={false} />
            <KPICard t={t} label="Net Funds Held"       value={fmtK(kpis.netFundsHeld)}   sub="cash collected − allocated costs" positive={kpis.netFundsHeld > 0} />
            <KPICard t={t} label="Underfunded Exposure" value={kpis.totalExposure < 0 ? `-${fmtK(Math.abs(kpis.totalExposure))}` : "$0"} sub={`${coreScorecard.filter(p => n(p.Exposure) < 0).length} projects underfunded`} alert />
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <SLabel t={t} noMargin>Project Health Scorecard</SLabel>
            <ResetSort show={sSortCol !== "Total_Revenue" || sSortDir !== "desc"} onClick={resetScoreSort} t={t} />
          </div>
          <ScrollTable headers={SCORECARD_COLS} rows={scorecardRows} footer={scorecardFooter} t={t} maxHeight="55vh" sortCol={sSortCol} sortDir={sSortDir} onSort={handleScoreSort} dark={dark} />
          <div style={{ marginTop: 52 }}>
            <SLabel t={t}>Cash Gap by Project</SLabel>
            <div style={{ display: "flex", gap: 24, marginBottom: 20, alignItems: "center" }}>
              {[{ color: t.black, label: "Collected" }, { dot: true, label: "Outstanding" }, { color: t.orange, label: "Underfunded" }].map(({ color, dot, label }) => (
                <div key={label} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 14, height: 10, flexShrink: 0, background: dot ? undefined : color, backgroundImage: dot ? `radial-gradient(circle, ${t.muted} 1.5px, transparent 1.5px)` : undefined, backgroundSize: dot ? "6px 6px" : undefined }} />
                  <span style={{ fontFamily: mono, fontSize: 9, color: t.muted, letterSpacing: "0.08em" }}>{label}</span>
                </div>
              ))}
            </div>
            {cashGapData.map((d, i) => <CashBar key={i} d={d} t={t} maxAmt={cashGapMax} />)}
          </div>
        </>}

        {/* ── COSTS ── */}
        {tab === "costs" && <>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <Sel t={t} value={projFilter} onChange={setProjFilter} options={projOptions} placeholder="All Projects" />
              <Sel t={t} value={billFilter} onChange={setBillFilter} options={["Billable", "NotBillable"]} placeholder="All Types" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search vendor, account..."
                style={{ fontFamily: mono, fontSize: 10, letterSpacing: "0.06em", color: t.text, background: t.bgCard, border: `1px solid ${t.faint}`, padding: "8px 14px", outline: "none", minWidth: 240 }} />
              {(projFilter || billFilter || search) && (
                <button onClick={() => { setProjFilter(""); setBillFilter(""); setSearch(""); }}
                  style={{ fontFamily: mono, fontSize: 9, color: t.muted, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", letterSpacing: "0.08em", textTransform: "uppercase" }}>Clear</button>
              )}
            </div>
            <button onClick={exportCSV} style={{ fontFamily: mono, fontSize: 9, letterSpacing: "0.1em", textTransform: "uppercase", padding: "9px 22px", background: t.black, color: dark ? t.bg : "#fff", border: "none", cursor: "pointer" }}>Export CSV</button>
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <SLabel t={t} noMargin>
              {sortedCosts.length} line items
              {isNoneFilter ? " · Unallocated" : projFilter ? ` · ${projFilter}` : ""}
              {billFilter ? ` · ${billFilter}` : ""}
            </SLabel>
            <ResetSort show={cSortCol !== "TxnDate" || cSortDir !== "desc"} onClick={resetCostSort} t={t} />
          </div>
          <ScrollTable headers={COST_COLS} rows={costRows} footer={costFooter} t={t} maxHeight="70vh" sortCol={cSortCol} sortDir={cSortDir} onSort={handleCostSort} dark={dark} />
        </>}

        {pageFooter}
      </div>
    </div>
  );
}